import { saveErrorToast } from "@components/CatalogIndex";

interface JsonApiError {
  detail?: string;
  source?: { pointer?: string };
}

// ProjectAttachment validates attachment_type_id unique per project, counting
// archived attachments too. JSON:API Resources reports it as
// detail "attachment-type-id - has already been taken" with pointer
// "/data/attributes/attachment-type-id" — hyphenated, so match on the key
// rather than the words "attachment type".
const TYPE_KEY = /attachment[-_ ]type(?:[-_ ]id)?/i;

export const isDuplicateTypeError = (err: unknown): boolean => {
  const { status, data } = (err ?? {}) as {
    status?: unknown;
    data?: { errors?: unknown };
  };
  const errors = data?.errors;
  if (status !== 422 || !Array.isArray(errors)) return false;
  return (errors as JsonApiError[]).some((e) => {
    const detail = e?.detail ?? "";
    const pointer = e?.source?.pointer ?? "";
    const aboutType =
      /attachment-type(?:-id)?$/.test(pointer) ||
      TYPE_KEY.test(detail.split(" - ")[0] ?? "");
    return aboutType && /already been taken/i.test(detail);
  });
};

export interface AttachmentTypeHolders {
  activeTypeIds: Set<string>;
  archivedTypeIds: Set<string>;
}

// The toast text for a failed attachment save. A duplicate-type rejection names
// where the type is held, since an archived holder is invisible on the Active
// tab and the type still shows in the picker.
export const attachmentSaveErrorText = (
  err: unknown,
  attachmentTypeId: string,
  holders: AttachmentTypeHolders
): string => {
  if (!isDuplicateTypeError(err)) {
    return saveErrorToast(err, "attachment").text;
  }
  const heldOnlyByArchived =
    holders.archivedTypeIds.has(attachmentTypeId) &&
    !holders.activeTypeIds.has(attachmentTypeId);
  return heldOnlyByArchived
    ? "Could not save attachment. An archived attachment on this project already uses this attachment type. Restore it from the Archived tab, or choose a different type."
    : "Could not save attachment. Another attachment on this project already uses this attachment type.";
};
